import {
  DOCUMENT_CATEGORIES as DOCUMENT_CATEGORY_NAMES,
  LEARNER_DOCUMENT_CATEGORIES as LEARNER_DOCUMENT_CATEGORY_NAMES,
  DOCUMENT_CATEGORY_CONFIG,
  DOCUMENT_CATEGORY_CONFIG_BY_ID,
  CATEGORY_HELPERS,
  RECENCY_LIMIT_DAYS,
  toCategoryId
} from '../constants/documentCategories';
import {
  DOCUMENT_ACCEPTED_FILE_EXTENSIONS,
  DOCUMENT_ACCEPTED_MIME_TYPES,
  MAX_DOCUMENT_SIZE_BYTES
} from '../constants/fileUploadLimits';

const toLegacyKey = (name) => toCategoryId(name).toUpperCase().replace(/-/g, '_');

const BOOLEAN_TRUE_VALUES = [true, 'true', 'yes', 'y', 1, '1'];

const isTruthy = (value) => {
  if (value === null || value === undefined) {
    return false;
  }
  if (typeof value === 'boolean') {
    return value;
  }
  if (typeof value === 'number') {
    return value !== 0;
  }
  return BOOLEAN_TRUE_VALUES.includes(String(value).toLowerCase());
};

const normaliseDate = (input) => {
  if (!input) {
    return null;
  }
  if (input instanceof Date) {
    return Number.isNaN(input.getTime()) ? null : input;
  }
  if (typeof input.toDate === 'function') {
    const dateValue = input.toDate();
    return Number.isNaN(dateValue.getTime()) ? null : dateValue;
  }
  const parsed = new Date(input);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const differenceInDays = (start, end) => {
  if (!start || !end) {
    return null;
  }
  const diff = end.getTime() - start.getTime();
  return Math.floor(diff / (1000 * 60 * 60 * 24));
};

const buildLegacyRecord = (config) => ({
  id: config.id,
  key: toLegacyKey(config.name),
  name: config.name,
  description: config.name,
  mandatory: config.mandatory,
  conditionalMandatory: config.conditionalMandatory || null,
  recencyRequired: config.recencyRequired,
  allowedVariants: config.allowedFileVariants || [],
  validationRules: {
    formats: [...DOCUMENT_ACCEPTED_FILE_EXTENSIONS],
    mimeTypes: [...DOCUMENT_ACCEPTED_MIME_TYPES],
    maxSizeBytes: MAX_DOCUMENT_SIZE_BYTES,
    recencyLimitDays: RECENCY_LIMIT_DAYS
  },
  helpText: CATEGORY_HELPERS[config.name] || null
});

export const DOCUMENT_CATEGORY_ORDER = DOCUMENT_CATEGORY_NAMES.map(toLegacyKey);

// Learner-specific document categories (Phase 1 - only 3 documents required)
export const LEARNER_DOCUMENT_CATEGORY_ORDER = LEARNER_DOCUMENT_CATEGORY_NAMES.map(toLegacyKey);

export const DOCUMENT_CATEGORIES = DOCUMENT_CATEGORY_NAMES.reduce((acc, name) => {
  const config = DOCUMENT_CATEGORY_CONFIG[name];
  const legacyRecord = buildLegacyRecord(config);
  acc[legacyRecord.key] = legacyRecord;
  return acc;
}, {});

export const DOCUMENT_CATEGORIES_BY_ID = Object.values(DOCUMENT_CATEGORIES).reduce(
  (acc, config) => {
    acc[config.id] = config;
    return acc;
  },
  {}
);

export const DOCUMENT_CATEGORIES_BY_NAME = DOCUMENT_CATEGORY_NAMES.reduce((acc, name) => {
  const config = DOCUMENT_CATEGORY_CONFIG[name];
  const legacyRecord = buildLegacyRecord(config);
  acc[name] = legacyRecord;
  return acc;
}, {});

export const DOCUMENT_DEADLINES = {
  INITIAL_UPLOAD: 14,
  REMINDER_SCHEDULE: [7, 3, 1],
  OVERDUE_ESCALATION: [1, 7, 14]
};

export const DOCUMENT_VALIDATION = {
  MAX_FILE_SIZE: MAX_DOCUMENT_SIZE_BYTES,
  ALLOWED_FORMATS: [...DOCUMENT_ACCEPTED_FILE_EXTENSIONS],
  ALLOWED_MIME_TYPES: [...DOCUMENT_ACCEPTED_MIME_TYPES],
  RECENCY_LIMIT_DAYS
};

const resolveCategoryConfig = (categoryKeyOrId) => {
  if (!categoryKeyOrId) {
    return null;
  }

  if (DOCUMENT_CATEGORIES[categoryKeyOrId]) {
    return DOCUMENT_CATEGORIES[categoryKeyOrId];
  }

  if (DOCUMENT_CATEGORIES_BY_ID[categoryKeyOrId]) {
    return DOCUMENT_CATEGORIES_BY_ID[categoryKeyOrId];
  }

  if (DOCUMENT_CATEGORIES_BY_NAME[categoryKeyOrId]) {
    return DOCUMENT_CATEGORIES_BY_NAME[categoryKeyOrId];
  }

  const upperKey = String(categoryKeyOrId).toUpperCase();
  if (DOCUMENT_CATEGORIES[upperKey]) {
    return DOCUMENT_CATEGORIES[upperKey];
  }

  const byId = DOCUMENT_CATEGORY_CONFIG_BY_ID[categoryKeyOrId];
  if (byId) {
    return buildLegacyRecord(byId);
  }

  return null;
};

export const getDocumentStatus = (document) => {
  if (!document) {
    return {
      status: 'missing',
      variant: 'danger',
      label: 'Missing',
      urgent: true
    };
  }

  if (document.status === 'verified' || document.reviewStatus === 'qa_approved') {
    return {
      status: 'verified',
      variant: 'success',
      label: 'Verified',
      urgent: false
    };
  }

  if (document.status === 'rejected' || document.reviewStatus === 'qa_rejected') {
    return {
      status: 'rejected',
      variant: 'danger',
      label: 'Rejected',
      urgent: true,
      comment:
        document.rejectionReason ||
        document.reviewNotes ||
        document.rejectionComment ||
        document.notes ||
        null
    };
  }

  if (document.status === 'pending') {
    return {
      status: 'pending',
      variant: 'warning',
      label: 'Pending Verification',
      urgent: false
    };
  }

  return {
    status: 'draft',
    variant: 'secondary',
    label: 'Supplied',
    urgent: false
  };
};

export const checkDocumentDeadline = (registrationDate, currentDate = new Date()) => {
  const regDate = normaliseDate(registrationDate) || new Date();
  const deadlineDate = new Date(regDate.getTime() + DOCUMENT_DEADLINES.INITIAL_UPLOAD * 24 * 60 * 60 * 1000);
  const daysUntilDeadline = Math.ceil((deadlineDate - currentDate) / (1000 * 60 * 60 * 24));

  return {
    deadlineDate,
    daysUntilDeadline,
    isOverdue: daysUntilDeadline < 0,
    isUrgent: daysUntilDeadline <= 3 && daysUntilDeadline >= 0,
    needsReminder: DOCUMENT_DEADLINES.REMINDER_SCHEDULE.includes(daysUntilDeadline)
  };
};

export const validateDocumentAge = (categoryKeyOrId, certificationDate) => {
  const config = resolveCategoryConfig(categoryKeyOrId);

  if (!config || !config.recencyRequired) {
    return { valid: true, ageInDays: null, limitInDays: RECENCY_LIMIT_DAYS };
  }

  const certifiedAt = normaliseDate(certificationDate);
  if (!certifiedAt) {
    return {
      valid: false,
      ageInDays: null,
      limitInDays: RECENCY_LIMIT_DAYS,
      reason: 'missingCertificationDate'
    };
  }

  const today = new Date();
  const ageInDays = differenceInDays(certifiedAt, today);

  if (ageInDays === null) {
    return {
      valid: false,
      ageInDays: null,
      limitInDays: RECENCY_LIMIT_DAYS,
      reason: 'invalidCertificationDate'
    };
  }

  return {
    valid: ageInDays <= RECENCY_LIMIT_DAYS,
    ageInDays,
    limitInDays: RECENCY_LIMIT_DAYS
  };
};

export const getConditionalMandatoryStatus = (categoryKeyOrId, userProfile) => {
  const config = resolveCategoryConfig(categoryKeyOrId);

  if (!config) {
    return false;
  }

  if (config.mandatory) {
    return true;
  }

  if (!config.conditionalMandatory) {
    return false;
  }

  const profile = userProfile || {};

  return config.conditionalMandatory.anyOf.some(({ field, values }) => {
    const profileValue = profile?.[field];

    return values.some((value) => {
      if (value === true) {
        return isTruthy(profileValue);
      }
      if (value === false) {
        return !isTruthy(profileValue);
      }
      if (typeof value === 'string') {
        return String(profileValue).toLowerCase() === value.toLowerCase();
      }
      return profileValue === value;
    });
  });
};

export default {
  DOCUMENT_CATEGORIES,
  DOCUMENT_CATEGORIES_BY_ID,
  DOCUMENT_DEADLINES,
  DOCUMENT_VALIDATION,
  getDocumentStatus,
  checkDocumentDeadline,
  validateDocumentAge,
  getConditionalMandatoryStatus
};
